/**
 * 사흘 — **서류·문서류 증거.**
 *
 * E04·E15·E16·E19·E20·E21·E22·E31·E34·E41·E42·E46·E47 은 전부 "종이"다. 그런데
 * 종이는 3D 에서 가장 만들기 쉬운 동시에 **가장 티나게 못 만드는** 물건이다 —
 * 두께 없는 판 한 장을 놓으면 즉시 「종이 그림」으로 읽힌다. 그래서 이 파일의
 * 물건은 전부 다음 셋 중 하나 이상을 반드시 가진다.
 *
 *  1. **묶음의 두께** — 낱장이 아니라 스물몇 장이 쌓인 블록. 옆면에 결이 보인다
 *  2. **훼손의 형태** — 찢김·탐·말림·들림. 무엇이 잘못됐는지가 실루엣에 있다
 *  3. **물린 흔적** — 클립·집게·스프링·압정. 종이가 무언가에 「걸려 있다」
 *
 * 인쇄면 규약:
 *   - 면은 `PlaneGeometry`(UV 0..1) 를 `thinSurface` 안에서 `w.add(geo, slot, 0)` 로 낸다.
 *     `uvTile > 0` 을 주면 `localUV` 가 UV 를 **미터 단위**로 덮어써서 텍스처 한 텍셀만
 *     샘플된다 — 이걸로 이미 한 번 순백색 종이가 나왔다
 *   - `ExtrudeGeometry` 로 만든 종이는 UV 가 미터로 나오므로 `sheetUV()` 로 0..1 로 되돌린다
 *   - **한글은 획으로만 긋는다.** 다만 **숫자·날짜·금액은 진짜 글자로 찍는다** — 그게
 *     단서의 본체이고(E34 날짜 불일치, E41 금액), 숫자는 어느 폰트에도 있어 두부가 안 난다
 */

import * as THREE from 'three';
import { bevelBox, lathe } from './kernel';
import { paintGeometry, type Piece } from './pieces';
import { printedFace, rnd, sheetUV, stroke, atlasUV, ribbon } from './saholEvidence';

/* ------------------------------------------------------------------ *
 * 종이 전용 도구
 * ------------------------------------------------------------------ */

/** 숫자·날짜·금액. 이것만은 읽혀야 한다 */
export function num(
  g: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  px: number,
  color = '#20293a',
  align: CanvasTextAlign = 'left',
) {
  g.fillStyle = color;
  g.font = `600 ${px}px "DejaVu Sans", Arial, Helvetica, sans-serif`;
  g.textAlign = align;
  g.textBaseline = 'alphabetic';
  g.fillText(text, x, y);
  g.textAlign = 'left';
}

/** 본문 몇 줄. 길이를 들쭉날쭉하게 해야 「글」로 읽힌다 — 다 같으면 줄무늬가 된다 */
export function body(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  wid: number,
  lines: number,
  lh: number,
  seed: number,
  color = 'rgba(38,48,64,0.66)',
) {
  g.strokeStyle = color;
  for (let i = 0; i < lines; i++) {
    const len = wid * (0.5 + rnd(seed + i * 3.7) * 0.5);
    stroke(g, x, y + i * lh, x + len, y + i * lh, Math.max(1.4, lh * 0.2), seed + i);
  }
}

/** 표 한 덩이. 서류가 서류로 보이는 가장 싼 방법이다 */
export function table(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  wid: number,
  hgt: number,
  rows: number,
  cols: number,
  seed: number,
) {
  g.strokeStyle = 'rgba(40,52,70,0.5)';
  g.lineWidth = 1.6;
  g.strokeRect(x, y, wid, hgt);
  for (let r = 1; r < rows; r++) {
    g.beginPath();
    g.moveTo(x, y + (hgt * r) / rows);
    g.lineTo(x + wid, y + (hgt * r) / rows);
    g.stroke();
  }
  for (let c = 1; c < cols; c++) {
    g.beginPath();
    g.moveTo(x + (wid * c) / cols, y);
    g.lineTo(x + (wid * c) / cols, y + hgt);
    g.stroke();
  }
  // 머리행은 채운다
  g.fillStyle = 'rgba(52,66,86,0.14)';
  g.fillRect(x, y, wid, hgt / rows);
  for (let r = 1; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (rnd(seed + r * 11 + c * 3) < 0.22) continue;
      const cw = wid / cols;
      body(g, x + cw * c + cw * 0.12, y + (hgt * (r + 0.62)) / rows, cw * 0.7, 1, 0, seed + r * 7 + c);
    }
  }
}

/** 결재란·직인. 서류 오른쪽 위의 이 상자 셋이 「공문서」 신호다 */
export function seal(g: CanvasRenderingContext2D, x: number, y: number, s: number, seed: number) {
  g.strokeStyle = 'rgba(40,52,70,0.55)';
  g.lineWidth = 1.6;
  for (let i = 0; i < 3; i++) g.strokeRect(x + i * s, y, s, s * 1.25);
  // 셋 중 하나에만 붉은 도장이 찍혀 있다 — 나머지 둘이 비었다는 게 요점인 서류가 많다
  g.save();
  g.globalAlpha = 0.72;
  g.strokeStyle = '#b6382b';
  g.lineWidth = s * 0.09;
  g.beginPath();
  g.arc(x + s * 0.5, y + s * 0.62, s * 0.33, 0, Math.PI * 2);
  g.stroke();
  g.beginPath();
  g.arc(x + s * 0.5, y + s * 0.62, s * 0.2, 0, Math.PI * 2);
  g.stroke();
  g.restore();
  void seed;
}

/** 한쪽 모서리가 뜯기거나 탄 낱장. 훼손이 실루엣에 있어야 조사가 성립한다 */
export function raggedSheet(wid: number, hgt: number, thick: number, seed: number) {
  const sh = new THREE.Shape();
  sh.moveTo(-wid / 2, -hgt / 2);
  sh.lineTo(wid / 2, -hgt / 2);
  sh.lineTo(wid / 2, hgt / 2 - rnd(seed) * hgt * 0.14);
  const n = 13;
  for (let i = n - 1; i >= 0; i--) {
    const x = -wid / 2 + (wid * i) / n;
    // 톱니의 깊이를 두 배로 흔든다. 균일하면 「지그재그 무늬」로 보이고 찢김으로 안 읽힌다
    const bite = rnd(seed + i * 5.3) * rnd(seed + i * 2.1) * hgt * 0.3;
    sh.lineTo(x, hgt / 2 - bite);
  }
  sh.lineTo(-wid / 2, -hgt / 2);
  const g = new THREE.ExtrudeGeometry(sh, { depth: thick, bevelEnabled: false });
  sheetUV(g, -wid / 2, -hgt / 2, wid, hgt);
  g.rotateX(-Math.PI / 2); // 셰이프 평면을 바닥에 눕힌다. 두께는 +Y 로 자란다
  g.rotateY(Math.PI); // 글의 위쪽을 +Z 로. 안 하면 -Z 에서 볼 때 뒤집혀 읽힌다
  return g;
}

/** 바닥에 눕는 인쇄면 한 장. `PlaneGeometry` 는 +Z 를 보므로 위(+Y)로 돌려 준다 */
export function facingUp(wid: number, dep: number) {
  const p = new THREE.PlaneGeometry(wid, dep);
  p.rotateX(-Math.PI / 2);
  // 보는 사람은 -Z 에 선다. rotateX 만 하면 글의 위쪽이 -Z 를 향해 **180도 뒤집혀 읽힌다**
  p.rotateY(Math.PI);
  return p;
}

export const PAPER_BG = '#efe9dc';

/* ------------------------------------------------------------------ *
 * 물건
 * ------------------------------------------------------------------ */

export const SAHOL_PAPER_PIECES: Piece[] = [
  {
    id: 'bugeom_seoryu',
    name: '예비 부검결과',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E04. 클립보드에 물린 예비 부검결과. 수사자료라 손이 여러 번 탄 물건이다 — 오른쪽 아래 모서리가 들려 있고 판지 모서리가 닳았다. 평평한 판 한 장이면 「종이 그림」이 되므로 묶음의 두께와 들린 모서리를 반드시 남긴다. 결재란 셋 중 하나만 도장이 찍혀 있다. */
    note: '판지 클립보드에 물린 서류 묶음. 위쪽 금속 집게, 묶음의 두께, 오른쪽 아래의 들린 모서리가 있어야 「클립보드에 물린 서류」로 읽힌다 — 평평한 판 한 장이면 「종이 그림」이 된다. 판지 모서리는 닳아 있고 첫 장 아래에 결재란 셋이 있으며 그 중 하나에만 도장이 찍혀 있다. dims 는 판 w·d, 판지 두께 board, 종이 묶음 두께 paper, 집게 폭 clip.',
    dims: { w: 0.232, d: 0.33, board: 0.007, paper: 0.004, clip: 0.078 },
    clusters: 1,
    /* 윗장(인쇄면·들린 모서리·맨 위 낱장)이 집게 축으로 넘어간다 — 클립보드의 첫 장을 젖히는 동작.
     * 집게는 +Z(먼 쪽)에 있다 */
    openable: [
      { part: '윗장', swing: 2.8, axis: 'x', pivot: [0, 0.011, 0.133] },
      { part: '펜', slide: [0, 0.04, 0] },
    ],
    build(w, d) {
      w.part('밑판');
      const board = bevelBox(d.w, d.board, d.d, 0.0025);
      board.translate(0, d.board / 2, 0);
      w.add(board, 'cardboard', 0.25);

      const pw = d.w - 0.014;
      const pd = d.d - 0.034;
      const pz = -0.006;
      const top = d.board + d.paper;

      w.part('서류');
      // 한 덩어리로 뽑으면 옆에서 「판때기」다. 네 겹을 살짝 어긋나게 쌓아야 옆면에 결이 생긴다
      for (let i = 0; i < 4; i++) {
        const sheet = bevelBox(pw - i * 0.0009, d.paper / 4, pd - i * 0.0011, 0.0005);
        sheet.rotateY((rnd(i * 9) - 0.5) * 0.012);
        sheet.translate((rnd(i * 3) - 0.5) * 0.0022, d.board + d.paper * (i + 0.5) / 4, pz + (rnd(i * 5) - 0.5) * 0.0022);
        // 맨 위 장은 넘기는 윗장의 몸이다 — 인쇄면·들린 모서리와 한 부품으로 움직인다
        if (i === 3) w.movable('윗장', () => w.add(sheet, 'paper', 0.2));
        else w.add(sheet, 'paper', 0.2);
      }

      w.part('인쇄면');
      w.movable('윗장', () => {
        w.thinSurface(() => {
          const p = facingUp(pw - 0.004, pd - 0.004);
          p.translate(0, top + 0.0007, pz);
          w.add(p, 'painted', 0);
        });
      });

      w.part('들린 모서리');
      // 낱장이 들리려면 「종이 두께 하나」가 아니라 두세 장이 같이 뜬다. 안 그러면 칼날처럼 보인다
      w.movable('윗장', () => {
        const curl = bevelBox(0.062, 0.0022, 0.056, 0.0006);
        curl.rotateX(-0.3);
        curl.rotateY(0.12);
        curl.translate(pw / 2 - 0.033, top + 0.0045, pz - pd / 2 + 0.026);
        w.add(curl, 'paper', 0.2);
      });

      w.part('클립');
      // 클립보드의 집게는 **종이의 먼 쪽 끝**(+Z)에 있다. 손 앞에 두면 글을 덮는다
      const cz = d.d / 2 - 0.032;
      const back = bevelBox(d.clip, 0.006, 0.036, 0.002);
      back.translate(0, d.board + 0.003, cz);
      w.add(back, 'steel', 0.3);
      // 스프링 배럴 — 매끈한 판 하나면 「회색 덩어리」다. 축이 있어야 집게로 읽힌다
      const barrel = lathe(
        [
          [0, 0],
          [0.0055, 0],
          [0.0055, d.clip],
          [0, d.clip],
        ],
        10,
      );
      barrel.rotateZ(Math.PI / 2);
      barrel.translate(d.clip / 2, d.board + 0.0088, cz + 0.008);
      w.add(barrel, 'steel', 0.12);
      const jaw = bevelBox(d.clip * 0.94, 0.0035, 0.03, 0.0012);
      jaw.rotateX(-0.16);
      jaw.translate(0, d.board + 0.0072, cz - 0.014);
      w.add(jaw, 'steel', 0.3);
      // 들어 올리는 지렛대 둘 — 위로 젖혀져 실루엣에 턱이 생긴다
      for (const sx of [-1, 1]) {
        const lever = bevelBox(0.016, 0.0025, 0.026, 0.0008);
        lever.rotateX(0.85);
        lever.translate(sx * d.clip * 0.28, d.board + 0.016, cz + 0.019);
        w.add(lever, 'steel', 0.2);
      }

      w.part('펜');
      w.movable('펜', () => {
        const pen = lathe(
          [
            [0, 0],
            [0.0055, 0.004],
            [0.0055, 0.126],
            [0.0032, 0.139],
            [0, 0.139],
          ],
          10,
        );
        pen.rotateZ(Math.PI / 2);
        pen.rotateY(0.34);
        pen.translate(0.028, top + 0.0045, pz + 0.052);
        w.add(pen, 'plastic', 0.2);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 위 17% 는 집게에 덮인다. 그 아래부터 인쇄가 시작돼야 숫자가 살아남는다
          g.fillStyle = 'rgba(150,150,140,0.08)';
          g.fillRect(0, H * 0.19, W, H * 0.11); // 머리띠
          num(g, '2024.12.19', W * 0.06, H * 0.265, H * 0.036);
          num(g, 'A-2419', W * 0.94, H * 0.265, H * 0.036, '#8a3b31', 'right');
          body(g, W * 0.06, H * 0.335, W * 0.5, 2, H * 0.03, 11);
          seal(g, W * 0.66, H * 0.31, W * 0.1, 3);

          table(g, W * 0.06, H * 0.44, W * 0.88, H * 0.22, 5, 3, 21);
          // 사망추정시각과 체온 강하 — 이 두 숫자가 E04 의 전부다
          num(g, '22:40', W * 0.09, H * 0.524, H * 0.03, '#1b2434');
          num(g, '02:10', W * 0.09, H * 0.577, H * 0.03, '#1b2434');
          num(g, '17.2', W * 0.41, H * 0.524, H * 0.03);
          num(g, '0.83', W * 0.41, H * 0.577, H * 0.03);

          body(g, W * 0.06, H * 0.7, W * 0.88, 5, H * 0.035, 44);
          g.strokeStyle = 'rgba(40,52,70,0.5)';
          g.lineWidth = 1.6;
          g.beginPath();
          g.moveTo(W * 0.55, H * 0.93);
          g.lineTo(W * 0.94, H * 0.93);
          g.stroke();
          body(g, W * 0.58, H * 0.905, W * 0.2, 1, 1, 90, 'rgba(30,40,58,0.8)');
        },
        640,
        900,
        PAPER_BG,
      ),
    }),
  },

  {
    id: 'archive_binder',
    nofront: true,
    name: '기록보관 바인더',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E19. 앞표지가 살짝 들린 채 눕혀진 3공 바인더. 안쪽에 **뜯겨 나간 페이지의 밑동**이 톱니처럼 남아 있고, 옆에 찢긴 조각 셋이 떨어져 있다. 페이지가 없다는 사실이 형태로 보여야 하므로 밑동과 조각을 절대 빼지 마라 — 멀쩡한 바인더를 놓고 설명문으로 때우면 조사가 텍스트 읽기가 된다. */
    note: '앞표지가 살짝 들린 채 눕혀진 3공 바인더. 안쪽 링에 **뜯겨 나간 페이지의 밑동**이 톱니처럼 남아 있고, 옆에 찢긴 조각 셋이 떨어져 있다 — 페이지가 뜯겼다는 사실이 형태로 보이는 조각이므로 밑동과 조각은 빼지 않는다. 멀쩡한 바인더가 필요하면 `binder_closed` 를 쓴다. dims 는 w·d·h 와 등 폭 spine.',
    dims: { w: 0.28, d: 0.315, h: 0.055, spine: 0.05 },
    clusters: 5,
    /* 앞표지는 등(-x) 경첩에서 100° 젖혀진 채 구워 있다 — E 를 누르면 덮인다.
     * 옆에 떨어진 찢긴 조각 셋은 하나씩 집을 수 있다 */
    openable: [
      { part: '앞표지', swing: -1.75, axis: 'z', pivot: [-0.086, 0.048, 0], inside: { w: 0.22, h: 0.035, d: 0.29, at: [0.01, 0.022, 0] } },
      { part: '찢긴 조각 1', slide: [0, 0.04, 0] },
      { part: '찢긴 조각 2', slide: [0, 0.04, 0] },
      { part: '찢긴 조각 3', slide: [0, 0.04, 0] },
    ],
    build(w, d) {
      const cov = 0.0035;

      w.part('아래 표지');
      const bot = bevelBox(d.w, cov, d.d, 0.0025);
      bot.translate(0, cov / 2, 0);
      w.add(bot, 'cardboard', 0.3);

      w.part('등');
      const spine = bevelBox(d.spine, d.h, d.d, 0.004);
      spine.translate(-d.w / 2 + d.spine / 2, d.h / 2, 0);
      w.add(spine, 'cardboard', 0.3);

      w.part('속지');
      const pw = d.w - d.spine - 0.016;
      const px = -d.w / 2 + d.spine + 0.008 + pw / 2;
      const blk = bevelBox(pw, d.h * 0.62, d.d - 0.014, 0.0015);
      blk.translate(px, cov + (d.h * 0.62) / 2, 0);
      w.add(blk, 'paper', 0.2);

      w.part('뜯긴 밑동');
      // 링에 걸린 채 찢겨 나간 자리. 밑동만 남으면 「빠진 페이지」가 실루엣이 된다
      for (let i = 0; i < 9; i++) {
        const st = raggedSheet(0.026, d.d - 0.03, 0.0012, i * 6 + 3);
        st.translate(
          -d.w / 2 + d.spine + 0.014,
          cov + d.h * 0.62 + 0.0009 + i * 0.0015,
          (rnd(i * 3) - 0.5) * 0.004,
        );
        w.add(st, 'paper', 0.2);
      }

      w.part('앞표지');
      // 경첩을 등 쪽 모서리에 두고 **수직을 조금 넘겨** 젖힌다. 23° 만 열면 위에서 볼 때
      // 표지가 속지·링·뜯긴 밑동을 통째로 덮어 「그냥 닫힌 바인더」가 되고,
      // 160° 까지 젖히면 이번엔 표지가 바닥에 눕는 판때기 둘이 된다(둘 다 실제로 찍어 봤다).
      // 100° 면 표지가 서서 벽이 되고 속지·링·밑동이 위에서 전부 드러난다
      const ang = 1.75;
      const cl = d.w - d.spine;
      const hx = -d.w / 2 + d.spine + 0.004;
      const hy = cov + d.h * 0.62 + 0.01;
      w.movable('앞표지', () => {
        const topc = bevelBox(cl, cov, d.d, 0.0025);
        topc.translate(cl / 2, 0, 0);
        topc.rotateZ(ang);
        topc.translate(hx, hy, 0);
        w.add(topc, 'cardboard', 0.3);

        w.thinSurface(() => {
          const p = facingUp(cl * 0.62, d.d * 0.3);
          p.translate(cl * 0.5, cov / 2 + 0.0012, -d.d * 0.24);
          p.rotateZ(ang);
          p.translate(hx, hy, 0);
          w.add(p, 'painted', 0);
        });
      });

      w.part('링');
      // 링이 속지보다 낮으면 종이 속에 파묻혀 안 보인다. 호가 종이 위로 2cm 올라와야 3공 바인더로 읽힌다
      for (let i = 0; i < 3; i++) {
        const ring = new THREE.TorusGeometry(0.029, 0.003, 6, 18);
        ring.rotateY(Math.PI / 2);
        ring.translate(-0.076, 0.031, -d.d * 0.3 + i * d.d * 0.3);
        w.add(ring, 'steel', 0.2);
      }

      w.part('찢긴 조각');
      const drop: [number, number, number, number][] = [
        [0.235, 0.19, 0.42, 7],
        [0.295, -0.1, -0.9, 23],
        [0.205, -0.185, 1.9, 41],
      ];
      for (const [di, [x, z, rot, seed]] of drop.map((v, i) => [i, v] as const)) {
        w.movable('찢긴 조각 ' + (di + 1), () => {
          const sc = raggedSheet(0.1 + rnd(seed) * 0.05, 0.13 + rnd(seed + 9) * 0.06, 0.0011, seed);
          sc.rotateY(rot);
          // 종이는 바닥에 완전히 붙지 않는다. 한쪽만 살짝 들리게 기울인다
          sc.rotateX(0.04 + rnd(seed + 3) * 0.05);
          sc.translate(x, 0.0016 + rnd(seed + 5) * 0.002, z);
          w.add(sc, 'paper', 0.2);
        });
      }
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.fillStyle = 'rgba(60,74,96,0.1)';
          g.fillRect(0, 0, W, H);
          g.strokeStyle = 'rgba(40,52,70,0.55)';
          g.lineWidth = 3;
          g.strokeRect(W * 0.05, H * 0.12, W * 0.9, H * 0.76);
          num(g, '2019 - 2024', W * 0.5, H * 0.44, H * 0.19, '#26303f', 'center');
          body(g, W * 0.14, H * 0.66, W * 0.72, 2, H * 0.11, 5);
        },
        512,
        200,
        '#ddd6c6',
      ),
    }),
  },

  {
    id: 'burnt_memo',
    name: '탄 점검메모',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E20. 금속 재떨이 위에서 태우다 만 점검메모. 위쪽 3분의 1이 타서 없어졌고 탄 자리는 검게 눌어붙어 있다 — 재와 눌은 테두리가 없으면 그냥 찢어진 종이다. 남은 아래쪽에 점검 항목표가 살아 있고 그 표의 마지막 줄만 읽을 수 있다. */
    note: '금속 재떨이 위에 놓인, 태우다 만 메모지. 위쪽 3분의 1이 타서 없어졌고 탄 자리는 검게 눌어붙어 있다 — 재와 눌은 테두리가 없으면 그냥 찢어진 종이다. 남은 아래쪽에는 인쇄된 항목표가 살아 있다. dims 는 종이 w·h, 재떨이 지름 dish, 재떨이 테두리 lip.',
    dims: { w: 0.148, h: 0.2, dish: 0.2, lip: 0.026 },
    clusters: 3,
    // 메모지를 집어 올려 뒤집어 볼 수 있다
    openable: [{ part: '탄 메모', slide: [0, 0.04, 0] }],
    build(w, d) {
      w.part('재떨이');
      const dish = lathe(
        [
          [0, 0],
          [d.dish / 2 - 0.004, 0],
          [d.dish / 2, 0.004],
          [d.dish / 2, d.lip],
          [d.dish / 2 - 0.0035, d.lip],
          [d.dish / 2 - 0.0045, 0.006],
          [0, 0.006],
        ],
        26,
      );
      w.add(dish, 'steel', 0.14);

      w.part('탄 메모');
      w.movable('탄 메모', () => {
        const sheet = raggedSheet(d.w, d.h * 0.66, 0.0013, 17);
        sheet.rotateY(0.14);
        sheet.translate(0.004, 0.0068, 0.012);
        w.add(sheet, 'painted', 0);
      });

      w.part('말린 조각');
      // 탄 종이는 불에서 멀어지는 쪽으로 **말린다**. 납작한 조각을 기울여 놓으면
      // 그냥 부스러기다 — 실루엣이 재떨이 테두리(lip) 위로 솟아야 「타다 만 것」이 된다
      for (let i = 0; i < 3; i++) {
        const R = 0.014 + rnd(i * 7) * 0.006;
        const yaw = 0.8 + i * 2.1 + rnd(i + 4);
        const ox = Math.cos(yaw) * (0.03 + rnd(i + 2) * 0.02);
        const oz = Math.sin(yaw) * (0.03 + rnd(i + 2) * 0.02);
        const pts: THREE.Vector3[] = [];
        for (let j = 0; j <= 7; j++) {
          const th = (j / 7) * (2.3 + rnd(i * 5) * 0.7);
          const lx = R * Math.sin(th);
          const ly = 0.0072 + R * (1 - Math.cos(th));
          pts.push(new THREE.Vector3(ox + Math.cos(yaw) * lx, ly, oz + Math.sin(yaw) * lx));
        }
        ribbon(w, pts, 0.026 + rnd(i * 3) * 0.016, 0.0012, 'coal');
      }

      w.part('재');
      // 고르게 흩뿌리면 「점 무늬」다. 태운 자리 한쪽에 몰리고 크기가 널뛰어야 재로 읽힌다
      for (let i = 0; i < 22; i++) {
        const a = -0.9 + rnd(i * 3.1) * 3.4;
        const r = (0.25 + rnd(i * 5.7) * 0.68) * d.dish * 0.44;
        const sz = 0.004 + rnd(i) * rnd(i * 2) * 0.017;
        const fl = bevelBox(sz, 0.0012 + rnd(i + 5) * 0.0022, sz * (0.5 + rnd(i + 3)), 0.0004);
        fl.rotateX((rnd(i + 11) - 0.5) * 0.9);
        fl.rotateY(rnd(i + 6) * 3.1);
        fl.translate(Math.cos(a) * r, 0.0066 + rnd(i + 7) * 0.003, Math.sin(a) * r);
        w.add(fl, 'coal', 0.1);
      }
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          body(g, W * 0.08, H * 0.16, W * 0.84, 3, H * 0.055, 3);
          table(g, W * 0.08, H * 0.36, W * 0.84, H * 0.38, 4, 3, 9);
          num(g, '12.16', W * 0.11, H * 0.7, H * 0.05, '#1e2735');
          num(g, '21:40', W * 0.44, H * 0.7, H * 0.05, '#1e2735');
          num(g, 'NG', W * 0.74, H * 0.7, H * 0.05, '#a8352a');
          body(g, W * 0.08, H * 0.82, W * 0.6, 2, H * 0.05, 71);

          // 위쪽으로 갈수록 눌어붙는다. 갈변 → 흑화 순서를 지켜야 「탄 것」으로 읽힌다
          const scorch = g.createLinearGradient(0, H * 0.62, 0, 0);
          scorch.addColorStop(0, 'rgba(110,70,30,0)');
          scorch.addColorStop(0.3, 'rgba(122,74,28,0.5)');
          scorch.addColorStop(0.58, 'rgba(70,38,16,0.9)');
          scorch.addColorStop(0.8, 'rgba(22,14,9,0.99)');
          scorch.addColorStop(1, 'rgba(8,6,5,1)');
          g.fillStyle = scorch;
          g.fillRect(0, 0, W, H * 0.62);
          // 불길이 고르게 올라가지 않는다 — 얼룩이 있어야 그라디언트로 안 보인다
          for (let i = 0; i < 34; i++) {
            const yy = rnd(i * 7) * H * 0.66;
            const dark = 1 - yy / (H * 0.66);
            g.globalAlpha = 0.35 + dark * 0.5;
            g.fillStyle = rnd(i) > 0.42 ? 'rgba(12,8,6,0.95)' : 'rgba(134,80,30,0.6)';
            g.beginPath();
            g.ellipse(rnd(i * 3) * W, yy, 10 + rnd(i * 2) * 52, 7 + rnd(i * 5) * 26, rnd(i) * 3, 0, 6.3);
            g.fill();
          }
          g.globalAlpha = 1;
          // 불이 멎은 경계 — 밝은 재선 하나가 있어야 「여기까지 탔다」가 읽힌다
          g.strokeStyle = 'rgba(228,206,168,0.55)';
          g.lineWidth = H * 0.006;
          g.beginPath();
          for (let x = 0; x <= W; x += W / 24) {
            const yy = H * (0.5 + Math.sin(x * 0.021) * 0.035 + rnd(x) * 0.03);
            x === 0 ? g.moveTo(x, yy) : g.lineTo(x, yy);
          }
          g.stroke();
        },
        480,
        640,
        '#e7e0d0',
      ),
    }),
  },

  {
    id: 'rolled_drawing',
    mount: 'surface',
    nofront: true,
    name: '수리도면',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E21. 반쯤 펼쳐진 청사진. 원통은 몸통이고, 바닥으로 흘러내린 자락이 진짜 정보다 — 자락이 없으면 그냥 종이 봉이다. 자락은 처음엔 원통 곡률을 따라 서 있다가 바닥에 닿으면서 평평해져야 한다. 고무줄이 원통 가운데를 물고 있다. */
    note: '고무줄로 묶인 채 반쯤 펼쳐진 도면 두루마리. 원통은 몸통이고, 바닥으로 흘러내린 자락이 「도면」임을 읽히게 한다 — 자락이 없으면 그냥 종이 봉이다. 자락은 처음엔 원통 곡률을 따라 서 있다가 바닥에 닿으면서 평평해진다. 고무줄이 원통 가운데를 물고 있다. dims 는 길이 w 와 원통 반지름 r.',
    dims: { w: 0.62, r: 0.036 },
    clusters: 1,
    // 펼쳐진 자락을 들어 볼 수 있고, 고무줄을 두루마리 끝으로 밀어 뺄 수 있다
    openable: [
      { part: '자락', slide: [0, 0.02, -0.15] },
      { part: '고무줄', slide: [-0.22, 0, 0] },
    ],
    build(w, d) {
      w.part('두루마리');
      const roll = lathe(
        [
          [0, 0],
          [d.r, 0.002],
          [d.r, d.w - 0.002],
          [0, d.w],
        ],
        22,
      );
      roll.rotateZ(Math.PI / 2);
      roll.translate(-d.w / 2, d.r, 0);
      // 청사진을 만 것이므로 원통 겉도 파란 도면이다. 흰 종이로 두면 「나무 봉」이 된다.
      // uvTile 0 — lathe 가 준 원통 UV 를 그대로 써야 무늬가 감긴다
      w.add(roll, 'painted', 0);

      w.part('말린 이음매');
      // 말린 종이의 바깥 끝단. 이 턱 하나가 「말려 있다」를 실루엣으로 만든다
      const seam = bevelBox(d.w - 0.01, 0.0022, 0.012, 0.0006);
      seam.rotateX(-0.5);
      seam.translate(0, d.r + Math.cos(0.5) * d.r * 0.96, Math.sin(0.5) * d.r * 0.96);
      w.add(seam, 'painted', 0.2);

      w.part('심');
      // 말린 종이는 가운데가 비어 보인다. 끝면에 어두운 심을 박아야 「말려 있다」가 된다
      for (const s of [-1, 1]) {
        const core = lathe(
          [
            [0, 0],
            [d.r * 0.34, 0],
            [d.r * 0.34, 0.006],
            [0, 0.006],
          ],
          16,
        );
        core.rotateZ((Math.PI / 2) * s);
        core.translate((s * (d.w - 0.003)) / 2, d.r, 0);
        w.add(core, 'cardboard', 0.1);
      }

      w.part('펼친 자락');
      // ⚠ 여기서 한 번 크게 틀렸다. 마디마다 bevelBox 를 깔고 `uvTile 0.24` 를 주면
      // `localUV` 가 UV 를 **미터 단위**로 덮어써서, 길이 2cm 짜리 마디는 도면의
      // 8% 짜리 띠 한 줄만 샘플한다 — 청사진이 통째로 사라지고 파란 판때기가 나왔다.
      // 자락은 **끊지 말고 한 장**으로 뽑고 UV 를 직접 적는다.
      const pts: THREE.Vector3[] = [];
      const steps = 26;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        if (t < 0.3) {
          // 원통을 감고 도는 구간. 시작점을 0.95r 로 잡아 두루마리 안에 묻는다
          const a = Math.PI * 0.1 + (t / 0.3) * Math.PI * 0.66;
          pts.push(new THREE.Vector3(0, d.r + Math.cos(a) * d.r * 0.95, -Math.sin(a) * d.r * 0.95));
        } else {
          const u = (t - 0.3) / 0.7;
          const z = -d.r * 0.95 - u * 0.4;
          // 접선에서 떨어져 바닥으로. 뒤쪽 절반은 완전히 눕는다
          const y = Math.max(0.0012, d.r * 0.66 * (1 - u * 2.1));
          pts.push(new THREE.Vector3(0, y, z));
        }
      }
      w.movable('자락', () =>
        w.thinSurface(() => {
          const hw = d.w * 0.46;
          const pos: number[] = [];
        const uvs: number[] = [];
        const push = (x: number, p: THREE.Vector3, u: number, v: number) => {
          pos.push(x, p.y, p.z);
          uvs.push(u, v);
        };
        for (let i = 0; i < pts.length - 1; i++) {
          const a = pts[i];
          const b = pts[i + 1];
          // 보는 사람은 -Z 에 선다 → 화면 왼쪽이 월드 +X, 화면 위쪽이 +Z.
          // u 는 +X→-X, v 는 두루마리(+Z, 화면 위)에서 1 이어야 글이 바로 선다
          const v0 = 1 - i / (pts.length - 1);
          const v1 = 1 - (i + 1) / (pts.length - 1);
          push(hw, a, 0, v0);
          push(-hw, a, 1, v0);
          push(-hw, b, 1, v1);
          push(hw, a, 0, v0);
          push(-hw, b, 1, v1);
          push(hw, b, 0, v1);
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
        geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geo.computeVertexNormals();
        w.add(geo, 'painted', 0);
        }),
      );

      w.part('고무줄');
      // 반지름을 0.98r 로 주면 고무줄이 두루마리 **안에** 들어가 안 보인다. 겉으로 물려야 한다
      w.movable('고무줄', () => {
        const band = new THREE.TorusGeometry(d.r + 0.0006, 0.0032, 6, 22);
        band.rotateY(Math.PI / 2);
        band.translate(-d.w * 0.18, d.r, 0);
        w.add(band, 'rubber', 0.1);
      });
    },
    materials: () => ({
      // 자락은 `localUV` 로 0.24m 마다 반복된다 — 청사진 격자는 반복돼도 티가 안 나는 몇 안 되는 무늬다
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(210,224,240,0.3)';
          g.lineWidth = 1;
          for (let i = 1; i < 24; i++) {
            g.beginPath();
            g.moveTo((W * i) / 24, 0);
            g.lineTo((W * i) / 24, H);
            g.stroke();
            g.beginPath();
            g.moveTo(0, (H * i) / 24);
            g.lineTo(W, (H * i) / 24);
            g.stroke();
          }
          // ⚠ 위 33% 는 두루마리에 감겨 보이지 않는다. 내용은 전부 그 아래로 내린다
          g.strokeStyle = 'rgba(226,238,252,0.95)';
          g.lineWidth = 4;
          g.strokeRect(W * 0.1, H * 0.42, W * 0.48, H * 0.36);
          g.strokeRect(W * 0.58, H * 0.42, W * 0.3, H * 0.2);
          g.strokeRect(W * 0.1, H * 0.82, W * 0.42, H * 0.13);
          g.lineWidth = 2.2;
          g.beginPath();
          g.moveTo(W * 0.1, H * 0.62);
          g.lineTo(W * 0.58, H * 0.62);
          g.moveTo(W * 0.32, H * 0.42);
          g.lineTo(W * 0.32, H * 0.78);
          g.moveTo(W * 0.58, H * 0.62);
          g.lineTo(W * 0.88, H * 0.62);
          g.stroke();
          // 문·개구부 — 호 하나면 방이 「평면도」로 읽힌다
          g.lineWidth = 1.8;
          for (const [ax, ay, a0] of [
            [0.32, 0.62, -Math.PI / 2],
            [0.58, 0.5, Math.PI],
          ] as [number, number, number][]) {
            g.beginPath();
            g.arc(W * ax, H * ay, W * 0.055, a0, a0 + Math.PI / 2);
            g.stroke();
          }
          // 치수선 — 이게 있어야 「도면」이고 없으면 격자무늬 벽지다
          g.strokeStyle = 'rgba(200,216,236,0.8)';
          g.lineWidth = 1.6;
          g.beginPath();
          g.moveTo(W * 0.1, H * 0.375);
          g.lineTo(W * 0.58, H * 0.375);
          g.moveTo(W * 0.94, H * 0.42);
          g.lineTo(W * 0.94, H * 0.78);
          g.stroke();
          num(g, '3600', W * 0.34, H * 0.365, H * 0.04, '#dceaf8', 'center');
          num(g, '2400', W * 0.9, H * 0.61, H * 0.04, '#dceaf8', 'right');
          num(g, '2024.11.28', W * 0.88, H * 0.985, H * 0.042, '#f0d9a6', 'right');
        },
        512,
        512,
        '#1d3550',
      ),
    }),
  },

  {
    id: 'contract_file',
    name: '계약 서류철',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E15·E16·E22. 잠금서류함에서 나온 마닐라 서류철. 색인 탭 셋 중 가운데 하나만 붉고, 그 안의 문서에만 결재 도장이 하나 빠져 있다. 겉표지는 멀쩡하고 속만 이상한 물건이라 **탭과 삐져나온 낱장**으로 안이 다르다는 걸 알려야 한다. */
    note: '색인 탭이 달린 마닐라 서류철. 겉표지는 멀쩡하고, 색인 탭 셋 중 가운데 하나만 붉으며 옆으로 낱장이 살짝 삐져나와 있다 — **탭과 삐져나온 낱장**이 있어야 「서류가 든 서류철」로 읽히고 안이 다르다는 것도 겉에서 보인다. dims 는 w·d·h.',
    dims: { w: 0.242, d: 0.325, h: 0.026 },
    clusters: 1,
    // 앞표지를 왼쪽 등 경첩으로 젖혀 속지를 연다
    openable: [
      {
        part: '앞표지',
        swing: 2.9,
        axis: 'z',
        pivot: [-0.121, 0.024, 0],
        inside: { w: 0.22, h: 0.02, d: 0.3, at: [0, 0.012, 0.002] },
      },
    ],
    build(w, d) {
      const cov = 0.0022;

      w.part('아래 표지');
      const bot = bevelBox(d.w, cov, d.d, 0.002);
      bot.translate(0, cov / 2, 0);
      w.add(bot, 'cardboard', 0.3);

      w.part('속지');
      const blk = bevelBox(d.w - 0.012, d.h - cov * 2, d.d - 0.01, 0.0012);
      blk.translate(0, cov + (d.h - cov * 2) / 2, 0.002);
      w.add(blk, 'paper', 0.2);

      w.part('삐져나온 낱장');
      // 급하게 끼운 종이는 반드시 한두 장이 삐져나온다. 이 두 장이 「손 탄 서류철」을 만든다
      for (let i = 0; i < 2; i++) {
        const s = bevelBox(d.w - 0.02, 0.0011, d.d + 0.016, 0.0004);
        s.rotateY(0.03 - i * 0.07);
        s.translate(0.004 - i * 0.006, cov + (d.h - cov * 2) * (0.34 + i * 0.4), 0.012 + i * 0.004);
        w.add(s, 'paper', 0.2);
      }

      w.part('앞표지');
      w.movable('앞표지', () => {
        const topc = bevelBox(d.w, cov, d.d, 0.002);
        topc.rotateX(-0.035);
        topc.translate(0, d.h - cov / 2, 0);
        w.add(topc, 'cardboard', 0.3);

        w.thinSurface(() => {
          const p = facingUp(d.w * 0.86, d.d * 0.86);
          p.rotateX(-0.035);
          p.translate(0, d.h + 0.0008, 0.001);
          w.add(p, 'painted', 0);
        });
      });

      w.part('색인 탭');
      for (let i = 0; i < 3; i++) {
        const tab = bevelBox(0.028, 0.0026, 0.052, 0.0008);
        tab.translate(d.w / 2 + 0.011, cov + 0.004 + i * 0.008, -d.d * 0.26 + i * d.d * 0.26);
        w.add(tab, i === 1 ? 'redcloth' : 'paper', 0.2);
      }

      w.part('집게');
      const clip = bevelBox(0.052, 0.02, 0.011, 0.0025);
      clip.translate(-d.w * 0.24, d.h * 0.5, -d.d / 2 + 0.006);
      w.add(clip, 'steel', 0.2);
      const arm = bevelBox(0.05, 0.0022, 0.03, 0.0006);
      arm.translate(-d.w * 0.24, d.h - cov, -d.d / 2 + 0.02);
      w.add(arm, 'steel', 0.2);
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.fillStyle = 'rgba(90,74,44,0.08)';
          g.fillRect(0, 0, W, H);
          g.strokeStyle = 'rgba(64,54,38,0.5)';
          g.lineWidth = 3;
          g.strokeRect(W * 0.08, H * 0.08, W * 0.84, H * 0.2);
          body(g, W * 0.13, H * 0.16, W * 0.6, 2, H * 0.06, 13, 'rgba(52,44,30,0.8)');
          num(g, '2024 - 07', W * 0.86, H * 0.24, H * 0.05, '#5b4a30', 'right');

          table(g, W * 0.08, H * 0.36, W * 0.84, H * 0.3, 4, 4, 33);
          num(g, '8,000', W * 0.12, H * 0.53, H * 0.04, '#33291b');
          num(g, '12.16', W * 0.52, H * 0.53, H * 0.04, '#33291b');
          seal(g, W * 0.5, H * 0.72, W * 0.13, 8);
          body(g, W * 0.08, H * 0.74, W * 0.34, 4, H * 0.05, 55, 'rgba(52,44,30,0.7)');
        },
        512,
        700,
        '#d8caa8',
      ),
    }),
  },

  {
    id: 'sales_ledger',
    mount: 'surface',
    nofront: true,
    name: '판매 정산 장부',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E41·E46. 펼쳐진 스프링 장부. 왼쪽 면은 2단계 선판매 집계표이고 오른쪽 면 마지막 줄에 손으로 덧쓴 금액이 하나 있다 — 인쇄된 합계와 손글씨 합계가 다르다. 펼친 책은 두 면이 살짝 V 로 서야 한다. 평평하게 놓으면 판때기 두 장이다. */
    note: '펼쳐진 스프링 장부. 두 면에 인쇄된 집계표가 있고 오른쪽 면 마지막 줄에 손으로 덧쓴 금액이 하나 있다. 가운데 스프링 코일이 「장부」를 읽히게 하고, 펼친 책은 두 면이 살짝 V 로 서야 한다 — 평평하게 놓으면 판때기 두 장이다. dims 는 펼친 w·d 와 두께 h.',
    dims: { w: 0.39, d: 0.27, h: 0.03 },
    clusters: 1,
    // 오른쪽 면을 스프링 경첩으로 넘겨 왼쪽 위로 젖힌다 — 장부 넘기기
    openable: [{ part: '오른쪽 면', swing: 3.0, axis: 'z', pivot: [0.006, 0.014, 0] }],
    build(w, d) {
      const half = d.w / 2 - 0.004;
      const tilt = 0.055;

      for (const s of [-1, 1] as const) {
        const side = () => {
          w.part(s < 0 ? '왼쪽 면' : '오른쪽 면');
          const blk = bevelBox(half, d.h * 0.5, d.d, 0.0015);
          blk.rotateZ(s * tilt);
          blk.translate((s * (half / 2 + 0.006)), d.h * 0.28 + (half / 2) * Math.sin(tilt), 0);
          w.add(blk, 'paper', 0.2);

          w.part(s < 0 ? '왼쪽 인쇄' : '오른쪽 인쇄');
          w.thinSurface(() => {
            const p = facingUp(half * 0.94, d.d * 0.9);
            p.rotateZ(s * tilt);
            p.translate(
              s * (half / 2 + 0.006) - s * Math.sin(tilt) * d.h * 0.26,
              d.h * 0.28 + (half / 2) * Math.sin(tilt) + Math.cos(tilt) * d.h * 0.26 + 0.0008,
              0,
            );
            w.add(p, 'painted', 0);
          });
        };
        if (s > 0) w.movable('오른쪽 면', side);
        else side();
      }

      w.part('표지');
      // 아래로 깔린 표지가 두 면을 이어 준다. 이게 없으면 조각이 둘로 갈라진다
      const cover = bevelBox(d.w, 0.0035, d.d + 0.008, 0.002);
      cover.translate(0, 0.0018, 0);
      w.add(cover, 'cardboard', 0.3);

      w.part('스프링');
      for (let i = 0; i < 15; i++) {
        const ring = new THREE.TorusGeometry(d.h * 0.42, 0.0018, 5, 12);
        ring.rotateY(Math.PI / 2);
        ring.rotateZ(0.5);
        ring.translate(0, d.h * 0.36, -d.d / 2 + 0.012 + (i * (d.d - 0.024)) / 14);
        w.add(ring, 'steel', 0.1);
      }
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          num(g, '2024', W * 0.06, H * 0.075, H * 0.038);
          body(g, W * 0.28, H * 0.07, W * 0.44, 1, 1, 4);
          table(g, W * 0.06, H * 0.12, W * 0.88, H * 0.66, 9, 4, 61);
          // 합계 두 개가 다르다. 인쇄된 것과 손으로 덧쓴 것 — E41 은 이 8천만 원이 전부다
          num(g, '80,000,000', W * 0.94, H * 0.83, H * 0.045, '#232c3c', 'right');
          g.save();
          g.rotate(-0.03);
          // 이 숫자가 E41 의 전부다. 본문과 같은 크기로 두면 표 속에 묻힌다
          g.save();
          g.rotate(-0.02);
          num(g, '112,400,000', W * 0.92, H * 0.945, H * 0.085, 'rgba(20,58,104,0.35)', 'right');
          num(g, '112,400,000', W * 0.915, H * 0.94, H * 0.085, '#1c4f8a', 'right');
          g.restore();
          g.restore();
          g.strokeStyle = 'rgba(28,79,138,0.8)';
          g.lineWidth = 3;
          g.beginPath();
          g.moveTo(W * 0.5, H * 0.86);
          g.lineTo(W * 0.94, H * 0.855);
          g.stroke();
        },
        560,
        760,
        '#f2ece0',
      ),
    }),
  },

  {
    id: 'promo_poster',
    mount: 'wall',
    name: '판매 홍보 포스터',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E42. 벽에 붙은 홍보 포스터. **원점이 걸이 지점이라 몸통은 y=0 아래로 내려간다** — hangs 와 sunk 를 둘 다 단다. 오른쪽 아래가 습기로 말려 떠 있고, 압정 네 개 중 하나가 빠져 있다. 큰 글씨의 「승인」과 아래 깨알 주석의 「예정」이 모순인 물건이므로 큰 글씨와 작은 글씨의 크기 차를 극단적으로 준다. */
    note: '벽에 압정으로 붙인 종이 포스터. **원점이 걸이 지점이라 몸통은 y=0 아래로 내려간다** — hangs 와 sunk 를 둘 다 단다. 오른쪽 아래가 습기로 말려 떠 있고, 압정 네 개 중 하나가 빠져 있어 「오래 붙어 있던 종이」가 된다. 큰 표제 글씨와 아래 깨알 주석의 크기 차를 극단적으로 준다. dims 는 w·h 와 종이 두께 t.',
    hangs: true,
    sunk: true,
    dims: { w: 0.5, h: 0.7, t: 0.0018 },
    clusters: 1,
    // 말린 모서리를 잡아 더 젖혀 볼 수 있다 — 윗변 경첩에서 바깥(-Z)으로
    openable: [{ part: '말린 모서리', swing: 0.6, axis: 'x', pivot: [0.15, -0.595, -0.002] }],
    build(w, d) {
      w.part('포스터');
      const sh = bevelBox(d.w, d.h, d.t, 0.0004);
      sh.translate(0, -d.h / 2, 0);
      w.add(sh, 'paper', 0.3);

      w.part('인쇄면');
      w.thinSurface(() => {
        const p = new THREE.PlaneGeometry(d.w - 0.002, d.h - 0.002);
        p.rotateY(Math.PI); // PlaneGeometry 는 +Z 를 본다. 정면(-Z)에 놓으려면 뒤집는다
        p.translate(0, -d.h / 2, -d.t / 2 - 0.0004);
        w.add(p, 'painted', 0);
      });

      w.part('말린 모서리');
      // 습기 먹은 포스터는 아래 모서리부터 뜬다. 이 한 조각이 「오래 붙어 있었다」를 만든다
      const curl = bevelBox(d.w * 0.3, d.h * 0.16, d.t * 1.6, 0.0006);
      curl.rotateX(0.42);
      curl.rotateZ(-0.1);
      curl.translate(d.w * 0.3, -d.h + d.h * 0.07, -d.h * 0.16 * 0.2);
      w.movable('말린 모서리', () => w.add(curl, 'paper', 0.3));

      w.part('압정');
      // 넷 중 오른쪽 아래가 빠져 있다. 그래서 그쪽이 말렸다 — 결함이 서로를 설명해야 한다
      const pins: [number, number][] = [
        [-d.w / 2 + 0.024, -0.024],
        [d.w / 2 - 0.024, -0.024],
        [-d.w / 2 + 0.024, -d.h + 0.024],
      ];
      for (const [x, y] of pins) {
        const head = lathe(
          [
            [0, 0],
            [0.0058, 0.0008],
            [0.0058, 0.0038],
            [0.0042, 0.005],
            [0, 0.005],
          ],
          12,
        );
        head.rotateX(Math.PI / 2);
        head.translate(x, y, -d.t / 2 - 0.0025);
        w.add(head, 'plastic', 0.1);
      }

      w.part('빈 압정 구멍');
      const hole = lathe(
        [
          [0, 0],
          [0.0022, 0],
          [0.0022, 0.0012],
          [0, 0.0012],
        ],
        8,
      );
      hole.rotateX(Math.PI / 2);
      hole.translate(d.w / 2 - 0.024, -d.h + 0.024, -d.t / 2 - 0.0004);
      w.add(hole, 'steel', 0.1);
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          const sky = g.createLinearGradient(0, 0, 0, H);
          sky.addColorStop(0, '#e8ddc6');
          sky.addColorStop(0.52, '#cdbb9c');
          sky.addColorStop(1, '#efe8da');
          g.fillStyle = sky;
          g.fillRect(0, 0, W, H);

          // 조감 렌더 흉내 — 사각형 몇 개면 「분양 홍보물」로 읽힌다
          g.fillStyle = 'rgba(84,92,84,0.55)';
          for (let i = 0; i < 7; i++) {
            const bw = W * (0.08 + rnd(i) * 0.1);
            const bh = H * (0.06 + rnd(i + 9) * 0.12);
            g.fillRect(W * 0.08 + i * W * 0.12, H * 0.46 - bh, bw, bh);
          }
          g.fillStyle = 'rgba(52,60,54,0.4)';
          g.fillRect(0, H * 0.46, W, H * 0.05);

          g.fillStyle = '#8f2f24';
          g.fillRect(W * 0.06, H * 0.06, W * 0.88, H * 0.13);
          num(g, '2 0 2 5', W * 0.5, H * 0.155, H * 0.1, '#f6ece0', 'center');
          body(g, W * 0.1, H * 0.24, W * 0.8, 2, H * 0.055, 3, 'rgba(46,36,28,0.9)');

          g.fillStyle = 'rgba(28,84,52,0.9)';
          g.fillRect(W * 0.1, H * 0.58, W * 0.36, H * 0.09);
          num(g, 'OK', W * 0.28, H * 0.652, H * 0.075, '#eef6ee', 'center');
          num(g, '8,000', W * 0.56, H * 0.652, H * 0.07, '#8f2f24');

          body(g, W * 0.1, H * 0.74, W * 0.8, 3, H * 0.045, 27);
          // 깨알 주석. 큰 글씨와 이 줄이 모순이라는 게 E42 다
          body(g, W * 0.1, H * 0.9, W * 0.78, 3, H * 0.022, 91, 'rgba(60,54,46,0.55)');
        },
        520,
        728,
        '#e6dcc8',
      ),
    }),
  },

  {
    id: 'memo_pad',
    name: '개인 메모지',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E31. 윤가람의 손바닥만 한 메모패드. 윗장이 뜯겨 나가 밑동 톱니가 남았고, 그 아래 장에 **눌린 자국**만 남아 있다 — 뜯긴 장의 내용이 아래에 눌려 찍힌 것이 단서다. 그래서 윗면 인쇄는 잉크가 아니라 옅은 음각처럼 보여야 한다. */
    note: '손바닥만 한 메모패드. 윗장이 뜯겨 나가 위쪽에 밑동 톱니가 남았고, 남은 윗면에는 앞 장의 필적이 **눌린 자국**으로 남아 있다 — 그래서 윗면 인쇄는 잉크가 아니라 옅은 음각처럼 보여야 한다. dims 는 w·d 와 묶음 두께 h.',
    dims: { w: 0.106, d: 0.148, h: 0.014 },
    clusters: 2,
    // 눌린 윗장을 들어 보고, 옆에 놓인 볼펜을 집을 수 있다
    openable: [
      { part: '윗장', slide: [0, 0.025, -0.05] },
      { part: '볼펜', slide: [0, 0.04, 0] },
    ],
    build(w, d) {
      w.part('뒷대');
      const back = bevelBox(d.w, 0.0028, d.d, 0.001);
      back.translate(0, 0.0014, 0);
      w.add(back, 'cardboard', 0.2);

      w.part('종이 묶음');
      const blk = bevelBox(d.w - 0.002, d.h - 0.004, d.d - 0.002, 0.0008);
      blk.translate(0, 0.0028 + (d.h - 0.004) / 2, 0);
      w.add(blk, 'paper', 0.2);

      w.part('눌린 윗장');
      w.movable('윗장', () =>
        w.thinSurface(() => {
          const p = facingUp(d.w - 0.005, d.d - 0.005);
          p.translate(0, d.h - 0.0004, 0);
          w.add(p, 'painted', 0);
        }),
      );

      w.part('뜯긴 밑동');
      // 스테이플 쪽에 남은 톱니. 이게 없으면 「그냥 새 메모지」다.
      // 글의 위쪽이 +Z 이므로 제본선도 +Z 쪽(먼 변)이다 — -Z 에 두면 글 밑에 스테이플이 박힌다
      for (let i = 0; i < 7; i++) {
        const st = raggedSheet(d.w / 7 - 0.001, 0.014, 0.0008, i * 9 + 5);
        st.rotateX(Math.PI); // 톱니가 종이 안쪽(-Z)을 향하게 뒤집는다
        st.translate(-d.w / 2 + d.w / 14 + (i * d.w) / 7, d.h + 0.0006, d.d / 2 - 0.006);
        w.add(st, 'paper', 0.2);
      }

      w.part('철심');
      for (let i = 0; i < 2; i++) {
        const st = bevelBox(0.012, 0.0016, 0.0032, 0.0004);
        st.translate(-d.w * 0.24 + i * d.w * 0.48, d.h + 0.0008, d.d / 2 - 0.0075);
        w.add(st, 'steel', 0.1);
      }

      w.part('볼펜');
      w.movable('볼펜', () => {
        const pen = lathe(
          [
            [0, 0],
            [0.0048, 0.003],
            [0.0048, 0.115],
            [0.0028, 0.126],
            [0, 0.126],
          ],
          10,
        );
        pen.rotateZ(Math.PI / 2);
        pen.rotateY(-0.42);
        pen.translate(0.006, d.h + 0.0038, 0.03);
        w.add(pen, 'plastic', 0.2);
        const clip = bevelBox(0.0022, 0.0034, 0.03, 0.0006);
        clip.rotateY(-0.42);
        clip.translate(0.041, d.h + 0.0075, 0.052);
        w.add(clip, 'steel', 0.1);
      });
    },
    materials: () => {
      const pad = printedFace(
        (g, W, H) => {
          // 눌린 자국은 잉크가 아니다. 밝은 쪽에 그림자, 어두운 쪽에 하이라이트를 어긋나게 둔다
          // 1px 어긋남은 10cm 짜리 메모지에서 아예 안 보인다. 3px 로 벌리고
          // 그림자를 진하게 깔아야 「눌린 자국」이 형태로 읽힌다
          const emboss = (fn: (c: CanvasRenderingContext2D) => void) => {
            g.save();
            g.translate(0, 3.2);
            g.strokeStyle = 'rgba(92,84,68,0.72)';
            fn(g);
            g.restore();
            g.save();
            g.translate(0, -2);
            g.strokeStyle = 'rgba(255,255,252,1)';
            fn(g);
            g.restore();
          };
          emboss((c) => {
            for (let i = 0; i < 6; i++) {
              const len = W * (0.42 + rnd(i * 5) * 0.4);
              stroke(c, W * 0.1, H * (0.2 + i * 0.1), W * 0.1 + len, H * (0.2 + i * 0.1), 4.2, i * 3);
            }
          });
          emboss((c) => {
            c.lineWidth = 5;
            c.beginPath();
            c.arc(W * 0.66, H * 0.72, W * 0.16, 0, Math.PI * 2);
            c.stroke();
          });
          num(g, '22:34', W * 0.11, H * 0.885, H * 0.085, 'rgba(96,88,70,0.7)');
          num(g, '22:34', W * 0.11, H * 0.879, H * 0.085, 'rgba(255,255,252,1)');
        },
        420,
        580,
        '#f6f2e6',
      );
      // 같은 캔버스를 범프로 한 번 더 쓴다 — 텍스처를 새로 굽지 않으므로 사실상 공짜인데
      // 빛이 눌린 골을 따라 꺾여서 음각이 각도에 따라 살아난다
      pad.bumpMap = pad.map;
      pad.bumpScale = 0.6;
      return { painted: pad };
    },
  },

  {
    id: 'order_slips',
    name: '주문·환불 전표',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E46·E47. 집게에 물린 전표 뭉치. 물린 뭉치는 **주문표**(크림색)이고, 그 옆에 빠져나온 **환불표 석 장**(붉은 테두리)이 펼쳐져 있다 — 환불 금액이 읽히지 않으면 이 물건은 존재 이유가 없다. 붉은 장을 뭉치 밑에 깔면 위에서 보이지 않는다. 낱장을 정확히 겹쳐 쌓으면 종잇장 하나로 보이므로 반드시 각도를 흩뜨린다. */
    note: '집게에 물린 전표 뭉치. 물린 뭉치는 크림색 **주문표**이고, 그 옆에 빠져나온 붉은 테두리의 **환불표 석 장**이 펼쳐져 있다. 낱장을 정확히 겹쳐 쌓으면 종잇장 하나로 보이므로 각도를 흩뜨리고, 붉은 장은 뭉치 밑에 깔지 않는다(위에서 보이지 않는다). dims 는 전표 w·d 와 장수 n.',
    dims: { w: 0.104, d: 0.19, n: 16 },
    clusters: 1,
    // 뭉치 맨 윗장을 들어 보고, 옆에 펼쳐진 환불표 석 장을 하나씩 집을 수 있다
    openable: [
      { part: '윗장', slide: [0, 0.02, -0.05] },
      { part: '환불표 1', slide: [0, 0.03, 0] },
      { part: '환불표 2', slide: [0, 0.03, 0] },
      { part: '환불표 3', slide: [0, 0.03, 0] },
    ],
    build(w, d) {
      const n = Math.round(d.n);
      w.part('전표');
      for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        const sl = bevelBox(d.w, 0.0009, d.d, 0.0004);
        // 부채꼴은 위로 갈수록 벌어진다. 아래가 눌려 있어야 「집게에 물렸다」로 읽힌다
        sl.rotateY((rnd(i * 3.3) - 0.5) * 0.06 + t * t * 0.34);
        sl.rotateX(-t * 0.03);
        sl.translate(-0.028 + t * t * 0.016, 0.0005 + i * 0.00085, t * 0.006);
        // 아랫단은 붉은 환불표가 섞여 있다 — 옆면에 붉은 결이 보여야 두께가 읽힌다
        if (i === n - 1) w.movable('윗장', () => w.add(sl, 'paper', 0.2));
        else w.add(sl, i < 5 ? 'redcloth' : 'paper', 0.2);
      }

      w.part('윗장 인쇄');
      w.movable('윗장', () =>
        w.thinSurface(() => {
          const p = facingUp(d.w - 0.003, d.d - 0.003);
          atlasUV(p, 0, 0, 2, 1);
          p.rotateY(0.34);
          p.translate(-0.012, 0.0005 + (n - 1) * 0.00085 + 0.0007, 0.006);
          w.add(p, 'painted', 0);
        }),
      );

      w.part('빠져나온 환불표');
      // 뭉치 옆으로 펼쳐 둔다. 뭉치 안에 끼워 두면 -1,240,000 이 영원히 안 보인다
      const loose: [number, number, number, number][] = [
        [0.086, 0.03, -0.32, 0.0011],
        [0.104, -0.028, -0.12, 0.0023],
        [0.07, -0.086, 0.28, 0.0035],
      ];
      for (const [li, [lx, lz, rot, ly]] of loose.map((v, i) => [i, v] as const)) {
        w.movable('환불표 ' + (li + 1), () => {
          const sl = bevelBox(d.w, 0.0011, d.d, 0.0004);
          sl.rotateY(rot);
          sl.translate(lx, ly + 0.00055, lz);
          w.add(sl, 'redcloth', 0.2);
          w.thinSurface(() => {
            const p = facingUp(d.w - 0.004, d.d - 0.004);
            atlasUV(p, 1, 0, 2, 1);
            p.rotateY(rot);
            p.translate(lx, ly + 0.0013, lz);
            w.add(p, 'painted', 0);
          });
        });
      }

      w.part('집게');
      // 팔 둘을 벌려 세우면 「곤충」이 된다. 불독 클립은 몸통 + 뒤로 눕는 지렛대 둘이다
      const cz = -d.d / 2 + 0.014;
      const jaw = bevelBox(0.038, 0.016, 0.014, 0.0022);
      jaw.translate(-0.028, 0.0095, cz);
      w.add(jaw, 'steel', 0.15);
      const spring = lathe(
        [
          [0, 0],
          [0.0042, 0],
          [0.0042, 0.04],
          [0, 0.04],
        ],
        10,
      );
      spring.rotateZ(Math.PI / 2);
      spring.translate(-0.028 + 0.02, 0.0172, cz);
      w.add(spring, 'steel', 0.12);
      for (const sx of [-1, 1]) {
        const lever = bevelBox(0.0028, 0.006, 0.03, 0.0008);
        lever.rotateX(1.15);
        lever.translate(-0.028 + sx * 0.013, 0.023, cz + 0.011);
        w.add(lever, 'steel', 0.15);
      }
    },
    materials: () => ({
      // 한 캔버스에 두 장. 왼칸 = 주문표(파랑), 오른칸 = 환불표(빨강).
      // 슬롯 예산이 6칸뿐이라 재질을 하나 더 쓰는 대신 아틀라스로 쪼갠다
      painted: printedFace(
        (g, W, H) => {
          const CW = W / 2;
          const cell = (ox: number, accent: string, mark: string, amount: string, amtColor: string) => {
            g.save();
            g.translate(ox, 0);
            g.fillStyle = '#f4ece4';
            g.fillRect(0, 0, CW, H);
            g.fillStyle = accent;
            g.fillRect(0, 0, CW, H * 0.1);
            num(g, mark, CW * 0.06, H * 0.082, H * 0.07, '#f6e8e4');
            num(g, '2024.12.18', CW * 0.94, H * 0.078, H * 0.045, '#f6e8e4', 'right');
            table(g, CW * 0.06, H * 0.16, CW * 0.88, H * 0.42, 5, 3, mark === 'R' ? 17 : 53);
            num(g, amount, CW * 0.94, H * 0.68, H * 0.062, amtColor, 'right');
            body(g, CW * 0.06, H * 0.76, CW * 0.6, 3, H * 0.05, mark === 'R' ? 39 : 91);
            g.strokeStyle = accent;
            g.lineWidth = 4;
            g.strokeRect(CW * 0.04, H * 0.03, CW * 0.92, H * 0.94);
            g.restore();
          };
          cell(0, '#33608f', 'O', '1,240,000', '#20456f');
          cell(CW, '#b0392c', 'R', '- 1,240,000', '#a8342a');
        },
        720,
        640,
        '#f4ece4',
      ),
    }),
  },

  {
    id: 'photo_print_set',
    mount: 'surface',
    nofront: true,
    name: '인화 사진 묶음',
    collection: 'sahol',
    category: '단서',
    /* 사건 메모: E34. 인화지 다섯 장이 봉투에서 쏟아져 흩어져 있다. 오른쪽 아래 **날짜 각인이 장마다 다르고**, 그 중 두 장만 하루 어긋나 있다 — 이게 단서의 전부이므로 각인 숫자는 반드시 읽혀야 한다. 사진끼리 같은 무늬면 복붙이 눈에 보이므로 아틀라스로 장마다 다른 그림을 준다. */
    note: '봉투에서 쏟아져 흩어진 인화지 다섯 장. 각 장 오른쪽 아래에 날짜 각인이 찍혀 있고 숫자는 읽힐 크기로 둔다. 사진끼리 같은 무늬면 복붙이 눈에 보이므로 아틀라스로 장마다 다른 그림을 준다. dims 는 사진 w·d 와 봉투 폭 env.',
    dims: { w: 0.152, d: 0.102, env: 0.176 },
    clusters: 2,
    // 흩어진 사진 다섯 장을 하나씩 집어 뒤집어 볼 수 있다
    openable: [
      { part: '사진 1', slide: [0, 0.03, 0] },
      { part: '사진 2', slide: [0, 0.03, 0] },
      { part: '사진 3', slide: [0, 0.03, 0] },
      { part: '사진 4', slide: [0, 0.03, 0] },
      { part: '사진 5', slide: [0, 0.03, 0] },
    ],
    build(w, d) {
      w.part('봉투');
      const env = bevelBox(d.env, 0.0055, d.env * 0.72, 0.002);
      env.rotateY(-0.24);
      env.translate(-0.055, 0.0028, 0.05);
      w.add(env, 'cardboard', 0.2);
      const flap = bevelBox(d.env, 0.0018, d.env * 0.34, 0.0012);
      flap.rotateY(-0.24);
      flap.rotateX(-0.5);
      flap.translate(-0.055 + Math.sin(0.24) * d.env * 0.5, 0.0075, 0.05 - Math.cos(0.24) * d.env * 0.42);
      w.add(flap, 'cardboard', 0.2);

      w.part('사진');
      const lay: [number, number, number, number, number][] = [
        [0.062, -0.035, 0.28, 0, 0],
        [0.028, 0.048, -0.62, 1, 0],
        [-0.028, -0.062, 1.15, 2, 0],
        [0.098, 0.038, -0.18, 0, 1],
        [-0.005, 0.008, 0.5, 1, 1],
      ];
      lay.forEach(([x, z, rot, col, row], i) => {
        w.movable('사진 ' + (i + 1), () => {
          const ph = bevelBox(d.w, 0.0007, d.d, 0.0003);
          ph.rotateY(rot);
          ph.translate(x, 0.0057 + i * 0.0009, z);
          w.add(ph, 'paper', 0.2);

          w.thinSurface(() => {
            const p = facingUp(d.w - 0.002, d.d - 0.002);
            atlasUV(p, col, row, 3, 2);
            p.rotateY(rot);
            p.translate(x, 0.0057 + i * 0.0009 + 0.0005, z);
            w.add(p, 'painted', 0);
          });
        });
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          const cw = W / 3;
          const ch = H / 2;
          const dates = ['2024.12.17', '2024.12.17', '2024.12.18', '2024.12.17', '2024.12.18', '2024.12.17'];
          for (let r = 0; r < 2; r++) {
            for (let c = 0; c < 3; c++) {
              const x = c * cw;
              const y = r * ch;
              const k = r * 3 + c;
              // 어두운 실내 사진 여섯 장. 형태를 정확히 알아볼 필요는 없고 「다 다르다」가 요점이다
              const sky = g.createLinearGradient(x, y, x, y + ch);
              sky.addColorStop(0, k % 2 ? '#2a3038' : '#3a3630');
              sky.addColorStop(1, k % 2 ? '#14181e' : '#1d1a16');
              g.fillStyle = sky;
              g.fillRect(x, y, cw, ch);
              g.fillStyle = 'rgba(198,176,132,0.32)';
              for (let i = 0; i < 5; i++) {
                const bw = cw * (0.1 + rnd(k * 9 + i) * 0.22);
                const bh = ch * (0.14 + rnd(k * 7 + i) * 0.4);
                g.fillRect(x + cw * (0.06 + rnd(k * 3 + i) * 0.8) - bw / 2, y + ch * 0.82 - bh, bw, bh);
              }
              g.fillStyle = 'rgba(240,214,150,0.5)';
              g.beginPath();
              g.ellipse(x + cw * (0.25 + rnd(k) * 0.5), y + ch * 0.3, cw * 0.1, ch * 0.09, 0, 0, 6.3);
              g.fill();
              g.fillStyle = 'rgba(0,0,0,0.34)';
              g.fillRect(x, y + ch * 0.8, cw, ch * 0.2);
              // 각인은 인화지에 광학적으로 새겨진 주황색이다. 흰색으로 쓰면 캡션으로 보인다
              num(g, dates[k], x + cw * 0.94, y + ch * 0.93, ch * 0.085, '#f2a03c', 'right');
              g.strokeStyle = 'rgba(255,255,255,0.06)';
              g.lineWidth = 3;
              g.strokeRect(x + 2, y + 2, cw - 4, ch - 4);
            }
          }
        },
        1080,
        720,
        '#101318',
      ),
    }),
  },

  /* ---------------------------------------------------------------- *
   * 기록보관실 채우기 — **E19 바인더를 열네 번 복제하면 안 된다**
   *
   * 「파일도 뭐 같은것만 있고 왜 다 반쯤은 열려있는거임」(2026-09-12, Chris).
   * `archive_binder` 는 E19 **그 자체**다 — 앞표지가 젖혀지고 페이지가 뜯긴 상태가
   * 단서의 본체인데, 그걸 선반 채우는 데 열네 번 돌려 썼더니 방 전체가 뜯긴 바인더
   * 전시장이 됐고 정작 E19 는 안 튄다. 채우는 것은 **평범한 서류철**이어야 한다.
   * ---------------------------------------------------------------- */
  {
    id: 'file_row',
    nofront: true,
    name: '서류철 줄',
    collection: 'sahol',
    category: '이야기',
    note:
      '선반 한 칸에 등을 밖으로 꽂아 세운 서류철 한 줄. 기록보관실 · 사진실 · 재무실의 배경이다.\n' +
      '**같은 것이 줄지어 서야 그 방이 기록보관실로 읽히고**, 그래야 그 사이에 끼운 조사물 하나가 ' +
      '눈에 안 띈다(가림 1단계). 다만 **줄마다 달라야 한다** — `seed` 를 바꾸면 권수·폭·높이·색·' +
      '기운 자리가 바뀐다. 같은 seed 를 두 선반에 주면 복제 티가 난다.\n' +
      '끝에 빈칸을 남기고 마지막 한두 권이 그리로 기울어야 「쓰는 선반」이 된다 — 꽉 채워 세우면 ' +
      '벽지 무늬가 된다. 등마다 라벨 띠와 칸 번호 색인 딱지(A-1·B-2 …)가 붙는다.',
    dims: { w: 0.62, h: 0.31, d: 0.26, n: 9, seed: 1 },
    clusters: 1,
    // 가운데 서류철 하나를 줄에서 뽑아 볼 수 있다
    openable: [{ part: '서류철', slide: [0, 0, -0.12] }],
    build(w, d) {
      const n = Math.max(3, Math.round(d.n));
      const sd = d.seed;
      /** 철의 등 색 — 크라프트·미색·남색·팥색·회청·올리브 */
      const SPINE = ['#b39a72', '#d3c7ad', '#354258', '#6b3a34', '#6f7a80', '#5d6350'];
      /* **순수 난수로 고르면 한 줄이 통째로 크라프트색이 된다** — seed 1 에서 실제로 그랬다
       * (2026-09-12 시트). 팔레트를 seed 만큼 돌려 **차례로** 쓰고 가끔 한 칸 더 건너뛴다:
       * 줄마다 시작색이 다르면서 한 줄 안에는 반드시 네 색 이상이 선다 */
      const pick = (i: number) => SPINE[(Math.round(sd * 2.7) + i + (rnd(sd + i * 3.3) > 0.72 ? 1 : 0)) % SPINE.length];

      /* 폭을 흔들어 나눈다. 합이 정확히 d.w 여야 선반 밖으로 안 삐져나간다 */
      const share: number[] = [];
      for (let i = 0; i < n; i++) share.push(0.55 + rnd(sd * 13 + i * 7.3));
      const sum = share.reduce((a, b) => a + b, 0);
      const gap = 0.0022;
      /* 끝의 빈칸 — 여기로 마지막 권들이 기운다. 이게 없으면 꽉 찬 벽지가 된다 */
      const slack = d.w * (0.06 + rnd(sd * 3.7) * 0.05);
      const avail = d.w - slack - gap * (n - 1);

      w.part('서류철');
      // 줄 가운데 하나가 빠진다 — 라벨 띠·색인 딱지도 같은 이름의 movable 에 붙여 한 몸으로 뺀다
      const pull = Math.min(4, n - 1);
      const labels: { x: number; y: number; bw: number; lean: number; i: number }[] = [];
      let x = -d.w / 2;
      for (let i = 0; i < n; i++) {
        const bw = (share[i] / sum) * avail;
        const bh = d.h * (0.82 + rnd(sd * 5.1 + i * 11.7) * 0.18);
        const bd = d.d * (0.88 + rnd(sd * 2.3 + i * 3.9) * 0.12);
        /* 마지막 둘만 빈칸 쪽으로 기운다. 전부 기울면 쓰러지는 중인 선반이 된다 */
        const lean = i >= n - 2 ? (0.1 + rnd(sd + i) * 0.12) * (n - i === 1 ? 1.6 : 1) : 0;
        const g = bevelBox(bw, bh, bd, 0.0022);
        g.translate(0, bh / 2, 0);
        if (lean) g.rotateZ(lean);
        // 등이 정면(-Z)에 오게 — 앞면을 z = -d.d/2 에 맞추므로 중심은 그보다 bd/2 뒤다
        g.translate(x + bw / 2, 0, -d.d / 2 + bd / 2);
        paintGeometry(g, pick(i));
        if (i === pull) w.movable('서류철', () => w.add(g, 'cardboard', 0.32));
        else w.add(g, 'cardboard', 0.32);
        labels.push({ x: x + bw / 2, y: bh, bw, lean, i });
        x += bw + gap;
      }

      /* 등에 붙은 라벨 띠 — 여기에 손글씨 획과 칸 번호가 있다. 띠가 없으면 색 벽돌 줄이다.
       * 정면은 -Z 규약이므로 띠도 -Z 면에 올린다 */
      w.part('등 라벨');
      for (const lb of labels) {
        const lw = lb.bw * 0.82, lh = 0.085;
        const band = new THREE.PlaneGeometry(lw, lh);
        band.rotateY(Math.PI);
        /* **칸 번호가 열 권 다 B-2 면 그것도 「같은 것만」이다.** 라벨 여섯 장을 3×2 아틀라스로
         * 한 장에 그려 두고 철마다 다른 칸을 쓴다 — 그림 한 장, draw call 하나 그대로 */
        const cell = Math.floor(rnd(sd * 11.3 + lb.i * 6.1) * 6) % 6;
        atlasUV(band, cell % 3, Math.floor(cell / 3), 3, 2);
        band.translate(0, lb.y * 0.62, -d.d / 2 - 0.0008);
        if (lb.lean) band.rotateZ(lb.lean);
        band.translate(lb.x, 0, 0);
        if (lb.i === pull) w.movable('서류철', () => w.thinSurface(() => w.add(band, 'painted', 0)));
        else w.thinSurface(() => w.add(band, 'painted', 0));
      }

      /* 칸 번호 색인 딱지 — 이야기가 선반을 「B-2」「A-4」「C-1」로 부른다(07_단서.md).
       * 그러니 선반에 번호가 **보여야** 그 말이 지시로 읽힌다. 등 위쪽 모서리에 붙인다 */
      w.part('색인 딱지');
      for (const lb of labels) {
        if (rnd(sd * 9.3 + lb.i * 4.1) > 0.55) continue;   // 절반쯤만 붙어 있다
        const tw = Math.min(lb.bw * 0.7, 0.028);
        const tag = bevelBox(tw, 0.022, 0.0012, 0.0004);
        tag.translate(0, lb.y * 0.88, -d.d / 2 - 0.0016);
        if (lb.lean) tag.rotateZ(lb.lean);
        tag.translate(lb.x, 0, 0);
        paintGeometry(tag, ['#c7503f', '#d9a327', '#3f7d52'][lb.i % 3]);
        if (lb.i === pull) w.movable('서류철', () => w.add(tag, 'paper', 0.02));
        else w.add(tag, 'paper', 0.02);
      }
    },
    materials: () => ({
      // 손으로 쓴 라벨 — 한글은 획으로만 긋는다(이 파일 머리말 규약). 칸 번호만 진짜 글자
      // 라벨 여섯 장을 3×2 로. 칸 번호는 이야기가 부르는 이름 그대로다(07_단서.md: B-2 · A-4 · C-1)
      painted: printedFace(
        (g, W, H) => {
          const CODES = ['A-1', 'A-4', 'B-2', 'B-5', 'C-1', 'D-3'];
          const cw = W / 3, ch = H / 2;
          for (let k = 0; k < 6; k++) {
            const ox = (k % 3) * cw, oy = Math.floor(k / 3) * ch;
            g.fillStyle = k % 2 ? '#efe9dc' : '#e7dcc6';
            g.fillRect(ox, oy, cw, ch);
            g.strokeStyle = 'rgba(40,48,64,0.5)';
            g.lineWidth = 2;
            g.strokeRect(ox + cw * 0.07, oy + ch * 0.1, cw * 0.86, ch * 0.8);
            stroke(g, ox + cw * 0.16, oy + ch * 0.34, ox + cw * (0.5 + rnd(k) * 0.3), oy + ch * 0.34, ch * 0.09, k * 3 + 1);
            stroke(g, ox + cw * 0.16, oy + ch * 0.56, ox + cw * (0.45 + rnd(k * 5) * 0.3), oy + ch * 0.56, ch * 0.075, k * 7 + 2);
            num(g, CODES[k], ox + cw * 0.84, oy + ch * 0.84, ch * 0.2, '#8a3428', 'right');
          }
        },
        540,
        440,
        '#efe9dc',
      ),
    }),
  },

  {
    id: 'binder_closed',
    nofront: true,
    name: '닫힌 서류 바인더',
    collection: 'sahol',
    category: '이야기',
    note:
      '책상·캐비닛 위에 눕혀 둔 평범한 3공 바인더. `archive_binder`(E19) 는 **앞표지가 젖혀지고 ' +
      '페이지가 뜯긴** 단서 그 자체이므로 배경으로 쓰면 안 된다 — 배경에는 이것을 쓴다.\n' +
      '두세 권이 조금씩 어긋나게 포개진다. 딱 맞춰 쌓으면 사람이 안 쓰는 소품이 된다.',
    dims: { w: 0.28, d: 0.315, h: 0.055, n: 2, seed: 1 },
    clusters: 2,
    /* 맨 위 바인더: 고무 밴드를 옆으로 밀어 빼고, 앞표지를 등 경첩으로 젖혀 속지를 연다 */
    openable: [
      { part: '고무 밴드', slide: [0.12, 0, 0] },
      {
        part: '표지',
        swing: 2.9,
        axis: 'z',
        pivot: [-0.14, 0.105, 0],
        inside: { w: 0.24, h: 0.045, d: 0.29, at: [0, 0.075, 0] },
      },
    ],
    build(w, d) {
      const n = Math.max(1, Math.round(d.n));
      const COVER = ['#33415a', '#6d3430', '#2f3b33', '#767d85'];
      let y = 0;
      for (let i = 0; i < n; i++) {
        const sd = d.seed * 3.3 + i * 7.1;
        const bh = d.h * (0.85 + rnd(sd) * 0.3);
        const ox = (rnd(sd + 1.7) - 0.5) * 0.03;
        const oz = (rnd(sd + 3.1) - 0.5) * 0.035;
        const yaw = (rnd(sd + 5.9) - 0.5) * 0.22;
        const col = COVER[Math.floor(rnd(sd + 8.3) * COVER.length) % COVER.length];

        w.part(`바인더${i + 1}`);
        const top = i === n - 1;
        // 표지 두 장 + 속지. 속지를 표지보다 들여야 「덮인 책」으로 읽힌다
        for (const [oy, th, slot, tint] of [
          [0, 0.0035, 'cardboard', col],
          [bh - 0.0035, 0.0035, 'cardboard', col],
        ] as const) {
          const g = bevelBox(d.w, th, d.d, 0.0025);
          g.translate(0, y + oy + th / 2, 0);
          g.rotateY(yaw);
          g.translate(ox, 0, oz);
          paintGeometry(g, tint);
          if (top && oy > 0) w.movable('표지', () => w.add(g, slot, 0.3));
          else w.add(g, slot, 0.3);
        }
        /* **속지는 표지보다 확실히 들어가야 한다** — 6mm 만 들여 놨더니 흰 종이 덩어리가
         * 표지를 삼켜 「책」이 아니라 「종이 뭉치」로 보였다(2026-09-12 시트).
         * 색도 순백이면 눈이 그리로 쏠린다. 실제 서류철 속지는 누런 미색이다 */
        const pg = bevelBox(d.w - 0.02, bh - 0.009, d.d - 0.02, 0.0015);
        paintGeometry(pg, '#ded4bf');
        pg.translate(0, y + 0.0035 + (bh - 0.009) / 2, 0);
        pg.rotateY(yaw);
        pg.translate(ox, 0, oz);
        w.add(pg, 'paper', 0.2);

        // 등 — 두꺼운 3공 바인더는 등이 둥글게 튀어나와 있다. 이게 없으면 그냥 책이다
        const sp = bevelBox(0.024, bh + 0.002, d.d, 0.009);
        sp.translate(-d.w / 2 - 0.008, y + bh / 2, 0);
        sp.rotateY(yaw);
        sp.translate(ox, 0, oz);
        paintGeometry(sp, col);
        w.add(sp, 'cardboard', 0.3);

        // 표지를 가로지르는 고무 밴드 — 3공 바인더를 「닫아 둔 것」으로 만든다
        const band = bevelBox(0.012, 0.0022, d.d + 0.026, 0.0008);
        band.translate(d.w * 0.28, y + bh + 0.0011, 0);
        band.rotateY(yaw);
        band.translate(ox, 0, oz);
        paintGeometry(band, '#2a2e36');
        if (top) w.movable('고무 밴드', () => w.add(band, 'rubber', 0.02));
        else w.add(band, 'rubber', 0.02);

        // 표지 라벨 — 위에서 내려다보는 물건이라 윗면에 붙는다
        w.part(`라벨${i + 1}`);
        const label = () =>
          w.thinSurface(() => {
            const p = facingUp(d.w * 0.52, d.d * 0.26);
            p.translate(0, y + bh + 0.0002, -d.d * 0.2);
            p.rotateY(yaw);
            p.translate(ox, 0, oz);
            w.add(p, 'painted', 0);
          });
        if (top) w.movable('표지', label);
        else label();
        y += bh + 0.0015;
      }
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.fillStyle = '#efe9dc';
          g.fillRect(0, 0, W, H);
          g.strokeStyle = 'rgba(40,48,64,0.45)';
          g.lineWidth = 3;
          g.strokeRect(W * 0.04, H * 0.1, W * 0.92, H * 0.8);
          stroke(g, W * 0.1, H * 0.42, W * 0.72, H * 0.42, H * 0.14, 5);
          num(g, '2025', W * 0.9, H * 0.76, H * 0.3, '#2b3346', 'right');
        },
        320,
        150,
        '#efe9dc',
      ),
    }),
  },
  /* ================================================================ */
  {
    id: 'seal_envelope',
    name: '봉인봉투',
    collection: 'sahol',
    category: '서류',
    note:
      '**크라프트 봉인봉투 — 문 안쪽에 세워 두거나 붙어 있는 큰 봉투** (사흘 v8 1-d — ' +
      'cc_hall 키 캐비닛 안의 도어록 관리자 코드 봉투). 덮개가 위 가장자리에서 젖혀 ' +
      '열리고, 빨간 봉인 스티커는 덮개와 같이 움직인다. lid 프롭(`piece` 형)으로 ' +
      '열면 `openLog` 가 공개 로그에 남는다.',
    dims: { w: 0.16, h: 0.22, d: 0.014 },
    build(w, d) {
      const { w: W, h: H, d: D } = d;
      // --- 몸통 — 앞뒤 두 장이 살짝 부푼 얇은 통
      w.part('몸통');
      w.at(0, H / 2, 0, () => {
        w.add(bevelBox(W, H, D, 0.002), 'cardboard', 0.03);
      });
      /* --- 덮개 — 위 가장자리(크리스)에서 앞면으로 접혀 내려온다.
       * 경첩은 몸통 꼭대기 모서리: pivot 을 그 자리에 두고 x 축으로 젖힌다 */
      w.movable('덮개', () => {
        w.at(0, H - 0.036, -D / 2 - 0.0018, () => {
          w.add(bevelBox(W * 0.94, 0.07, 0.003, 0.0012), 'cardboard', 0.03);
          // 봉인 스티커 — 덮개를 뜯어야 안이 보인다는 표시
          const seal = new THREE.CylinderGeometry(0.015, 0.015, 0.0024, 16);
          seal.rotateX(Math.PI / 2);
          seal.translate(0, -0.008, -0.0028);
          w.add(seal, 'redcloth', 0.1);
        });
      });
    },
    openable: [{ part: '덮개', swing: 2.4, axis: 'x', pivot: [0, 0.216, -0.002] }],
  },
];
